import React, { useState } from 'react';

import { appConfig } from '../../../config/app-config';
import { formatPrice } from '../../../shared/utils/format';
import DiscountCodeInput from '../../discount/components/DiscountCodeInput';
import { useCheckoutDiscountContext } from '../../discount/context/useCheckoutDiscount';
import { TaxLineItem } from '../../tax';

import LineItemsPreview from './line-items-preview';

const OrderSummary = ({
    lineItems = [],
    subtotal,      // { centAmount, currencyCode }
    totalDiscount, // { centAmount, currencyCode }
    total,         // { centAmount, currencyCode } (Cart Total from SDK)
    tax,           // Object containing { taxAmount: number, grandTotal: number, taxPortions: [] }
    appliedDiscounts = [],
}) => {
    const locale = appConfig.defaults.locale;
    const [isExpanded, setIsExpanded] = useState(false);
    const {
        handleApplyDiscount,
        handleRemoveDiscount,
        discountError,
        applyingDiscount,
        setDiscountError,
    } = useCheckoutDiscountContext();

    // Fetch product metadata for add-ons



    const format = (priceObj) => {
        if (!priceObj || !priceObj.currencyCode) return '-';
        return formatPrice(priceObj, locale);
    };

    const hasDiscount = totalDiscount && totalDiscount.centAmount > 0;
    const currencyCode = total?.currencyCode || 'USD';

    const hasTaxData =
        tax && Array.isArray(tax.aggregatedTaxLines) && tax.aggregatedTaxLines.length > 0;

    // Grand total: use tax.grandTotal if available, else fall back to cart total.
    let finalTotalDisplay = format(total);
    if (tax?.grandTotal) {
        finalTotalDisplay = formatPrice(
            { centAmount: Math.round(Number(tax.grandTotal) * 100), currencyCode },
            locale
        );
    }

    const ChevronDownIcon = ({ className }) => (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    return (
        <div className={`w-[335px] min-[576px]:w-[410px] ${isExpanded ? 'h-fit' : 'h-[60px]'} min-[576px]:h-fit border border-gray-200 rounded-[8px] min-[576px]:rounded-[12px] bg-[#F9FAFB] box-border font-noto text-[#111827] flex flex-col gap-[16px] min-[576px]:gap-[2px] transition-all duration-300 overflow-hidden mx-auto`}
            style={{ boxShadow: 'var(--Level1PositionX) var(--Level1PositionY) var(--Level1Blur) var(--Level1Spread) var(--Level1ShadowElevationLevel1)' }} >

            {/* Mobile Header - Toggle Accordion */}
            <div
                className="flex min-[576px]:hidden justify-between items-center px-[16px] py-[12px] h-[58px] box-border cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <h3 className="font-aleo text-[16px] font-semibold m-0 text-[#182230]">Order summary</h3>
                <div className="w-[36px] h-[36px] rounded-[100px] bg-[#FCFCFD] flex items-center justify-center">
                    <ChevronDownIcon className={`w-5 h-5 text-[#000F1A] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {/* Desktop Header - Always visible on desktop inside the main padding */}
            {/* Wrapper for content - Hidden on mobile unless expanded, always visible on desktop */}
            <div className={`${isExpanded ? 'block border-gray-200' : 'hidden'} min-[576px]:block px-[16px] pb-[12px] pt-[0px] md:pt-[24px] md:px-8 md:py-6 md:border-none`}>

                {/* Desktop Header Content (hidden on mobile) */}
                <div className="hidden min-[576px]:flex justify-between items-center mb-6">
                    <h3 className="font-aleo text-[18px] font-semibold m-0 text-[#182230]">Order summary</h3>
                </div>



                <div className="mb-4 min-[576px]:mb-6">
                    <LineItemsPreview lineItems={lineItems} />
                </div>

                {!hasTaxData && appliedDiscounts.length === 0 && (
                    <div className="mb-[6px]">
                        <span style={{
                            color: 'var(--Neutral_grey-700, #344054)',
                            fontFamily: 'var(--1_Typography-Body-Sm-Sm_Medium-Typeface, "Noto Sans")',
                            fontSize: 'var(--1_Typography-Body-Sm-Sm_Medium-Font_size, 14px)',
                            fontStyle: 'normal',
                            fontWeight: 'var(--1_Typography-Body-Sm-Sm_Medium-Font_weight, 500)',
                            lineHeight: 'var(--1_Typography-Body-Sm-Sm_Medium-Line_height, 24px)'
                        }} className="m-0">Promotion Code</span>
                    </div>
                )}

                {(!hasTaxData || appliedDiscounts.length > 0) && (
                    <DiscountCodeInput
                        activeCodes={appliedDiscounts.map((d) => ({
                            code: d.code,
                            discount: d.percentage
                                ? (d.isProductDiscount
                                    ? `-${d.percentage}% applied for product`
                                    : `-${d.percentage}% discount`)
                                : 'Discount applied',
                            onRemove: hasTaxData ? undefined : () => handleRemoveDiscount(d.id)
                        }))}
                        onApply={handleApplyDiscount}
                        showActiveCodes={true}
                        error={discountError}
                        isLoading={applyingDiscount}
                        onClearError={() => setDiscountError(null)}
                    />
                )}

                <div className="mt-[32px] flex flex-col gap-[4px]">
                    {hasDiscount && (
                        <div className="flex justify-between items-center text-[14px] font-normal text-[#667085]">
                            <span>Total Discount:</span>
                            <span className="font-normal text-[#667085] flex items-center gap-[4px]">
                                - {format(totalDiscount)}
                            </span>
                        </div>
                    )}

                    <div className="flex justify-between items-center text-[14px] font-normal text-[#667085]">
                        <span>Sub Total:</span>
                        <span className="font-normal text-[#667085]">{format(subtotal)}</span>
                    </div>

                    {/* Per-authority tax rows from One Source */}
                    <TaxLineItem
                        taxLines={tax?.aggregatedTaxLines ?? []}
                        currencyCode={currencyCode}
                        locale={locale}
                    />

                    <div className="flex justify-between items-center mt-4 border-gray-100 font-aleo text-[#182230]">
                        <span className="text-[24px] font-semibold">Total</span>
                        <span className="text-[18px] font-semibold text-right">{finalTotalDisplay}</span>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default OrderSummary;

